import { TakeableChannel } from '@redux-saga/core';
import { ActionPattern } from '@redux-saga/core/effects';
import { Action } from 'redux';
import { Task } from 'redux-saga';
import { call, cancel, fork, take } from 'typed-redux-saga';

type KeyType = string | number | boolean;

/**
 * Метод как takeLatest, но с возможностью группировать экшены по ключу.
 *
 * Для каждого ключа создается свой форк, который отменяется, если до завершения его выполнения приходит другой экшен с
 * таким же ключом.
 *
 * @param {ActionPattern<A> | TakeableChannel<A>} patternOrChannel селектор экшенов (аналогично takeLatest)
 * @param {(action: A) => unknown} worker генератор (аналогично takeLatest)
 * @param {(action: A) => K} selector селектор ключа, по которому будет происходить группировка
 */
export function* takeLatestPerKey<A extends Action, K extends KeyType>(
    patternOrChannel: ActionPattern<A> | TakeableChannel<A>,
    worker: (action: A) => unknown,
    selector: (action: A) => K
) {
    const tasksMap = new Map<K, Task>();
    while (true) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const action = yield* take<A>(patternOrChannel as any);
        const key = selector(action);

        const lastTask = tasksMap.get(key);

        if (lastTask) {
            yield* cancel(lastTask); // cancel is no-op if the task has already terminated
        }

        const task = yield* fork(function* () {
            try {
                yield* call(worker, action);
            } finally {
                tasksMap.delete(key);
            }
        });

        tasksMap.set(key, task);
    }
}

/**
 * Метод как takeLeading, но с возможностью группировать экшены по ключу.
 *
 * Для каждого ключа создается свой форк, до завершения которого остальные экшены с таким же ключом будут отбрасываться.
 *
 * @param {ActionPattern<A> | TakeableChannel<A>} patternOrChannel селектор экшенов (аналогично takeLeading)
 * @param {(action: A) => unknown} worker генератор (аналогично takeLeading)
 * @param {(action: A) => K} selector селектор ключа, по которому будет происходить группировка
 */
export function* takeLeadingPerKey<A extends Action, K extends KeyType>(
    patternOrChannel: ActionPattern<A> | TakeableChannel<A>,
    worker: (action: A) => unknown,
    selector: (action: A) => K
) {
    const tasksMap = new Map<K, Task>();
    while (true) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const action = yield* take<A>(patternOrChannel as any);
        const key = selector(action);

        const lastTask = tasksMap.get(key);

        if (lastTask) {
            return;
        }

        const task = yield* fork(function* () {
            try {
                yield* call(worker, action);
            } finally {
                tasksMap.delete(key);
            }
        });

        tasksMap.set(key, task);
    }
}
